import { formatRoundDate, formatRoundLabel } from '@nemo/core/model';
import { Link } from 'wouter';
import type { Candidate } from '../dealroom/types.js';

/**
 * Why a candidate ranks where it does: the funds on our list it has sat on a
 * round beside (the ones somebody here knows first, each a way into its pane),
 * and the Project A portfolio companies it backed. The rounds behind both are
 * on hover. The portfolio comes first because it weighs most, and a narrow row
 * cuts the fund names rather than it. Nothing at all for a name with neither.
 */
export function CoInvestLine(props: {
  candidate: Candidate;
  /** How many fund names to spell out before "+N". */
  names?: number;
  /** Inside the swipe deck, where a link must not start a drag. */
  inDeck?: boolean;
}): React.JSX.Element | null {
  const { candidate } = props;
  const shownCount = props.names ?? 2;
  const shown = candidate.coInvestedFunds.slice(0, shownCount);
  const more = candidate.coInvestedFundCount - shown.length;
  const portfolio = candidate.portfolioCompanies.length;
  if (shown.length === 0 && portfolio === 0) return null;

  return (
    <span className="co-invest" title={sharedRoundsText(candidate)}>
      {portfolio > 0 && (
        <span className="co-invest-portfolio">
          Backed {portfolio} Project A portfolio {portfolio === 1 ? 'company' : 'companies'}
        </span>
      )}
      {shown.length > 0 && (
        <span className="co-invest-funds">
          Co-invested with{' '}
          {shown.map((fund, at) => (
            <span key={fund.fundId}>
              {at > 0 && ', '}
              <Link
                href={`/?fund=${fund.fundId}`}
                className={fund.inCrm ? 'co-invest-fund crm' : 'co-invest-fund'}
                {...(props.inDeck === true ? { draggable: false } : {})}
              >
                {fund.name}
              </Link>
            </span>
          ))}
          {more > 0 && ` +${more}`}
        </span>
      )}
    </span>
  );
}

/** One line a shared round, newest first: "Proxima · Series B · Jul 2026 — with Accel". */
export function sharedRoundsText(candidate: Candidate): string {
  // The api sends the newest dozen; the counts on the line are the whole of it.
  const lines = candidate.sharedRounds.map((round) =>
    [
      [round.companyName, formatRoundLabel(round.roundLabel), formatRoundDate(round)]
        .filter((part) => part !== '')
        .join(' · '),
      round.portfolio ? ' (Project A portfolio)' : '',
      round.funds.length > 0 ? ` — with ${round.funds.join(', ')}` : '',
    ].join(''),
  );
  return lines.join('\n');
}
